import { requireAuth } from '@/lib/auth/guards'

/**
 * The gate on the signed-in app, in its own module so a test can mount it.
 *
 * Not in app.tsx with the route it belongs to, because a test that imported
 * that file would pull `createFileRoute` into the test TS project — where the
 * generated `FileRoutesByPath` augmentation does not exist, since
 * src/routeTree.gen.ts is uncommitted and no test may import it
 * (vitest.config.ts says why). `createFileRoute('/_app')` then fails to
 * type-check against an empty interface. Splitting the guard off is what lets
 * the guard be tested at all.
 *
 * One question: is there a session. That is all — `requireAuth` asks the
 * auth store (which, the first time in a tab with no access token, asks the
 * refresh cookie once) and redirects an anonymous visitor to /signin, carrying
 * `location.href` so the sign-in form can put them back.
 *
 * This guard used to also fetch the onboarding persona, on the theory that
 * doing it here would "warm the cache" `useOnboarding` reads on the
 * onboarding screens. It didn't: nothing outside those screens reads that
 * cache entry, so the fetch bought nothing and cost a round trip on every
 * signed-in navigation — one that fails outright wherever mm-be's onboarding
 * route is down, and fails on EVERY navigation, because a failed query caches
 * no data and `appBeforeLoad` runs before every route inside this layout. The
 * persona fetch now runs only where its result is read: on the onboarding
 * screens, via `useOnboarding`.
 *
 * A session that dies server-side (the refresh token itself is rejected) is
 * no longer caught here either. It is caught wherever a query on the landed
 * route actually runs: the api client refreshes once and, when that fails,
 * signs the member out (see `withSessionRenewal` in api/client.ts), and the
 * sign-out watcher wired in main.tsx sends them to /signin — carrying the
 * page they were on as `search.redirect`, as this guard does, because the
 * store records that session as `expired` rather than ended by the member.
 */
export const appBeforeLoad = ({ location }: { location: { href: string } }): Promise<void> =>
  requireAuth({ location })
